import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ModelSettingsResponse {
  current: string;
  /**
   * ISO instant the model row was last written. Null when the value comes from
   * the env default, and absent entirely on a backend that predates this - the
   * portal ships independently, so an older API is a state to survive, not an
   * error.
   */
  changedAt?: string | null;
  configurable: boolean;
  available: string[];
}

export interface SystemPromptResponse {
  prompt: string | null;
  /**
   * ISO instant the current revision was written. Null when none exists, and
   * absent on a backend that predates this - see changedAt above.
   */
  promptSavedAt?: string | null;
  model: ModelSettingsResponse;
  promptVariables: string[];
}

/**
 * What the breaker in wrebit-bookkeep has observed about the provider.
 *
 * Read from whichever replica served the request - the breaker lives in memory
 * there, and prod runs two. During a real outage they reach the same
 * conclusion within a minute because they see the same failures, so treat this
 * as "last observed" rather than as global truth.
 */
export interface ProviderState {
  status: 'ok' | 'down';
  since?: string;
  reason?: string;
}

/**
 * Whether the AI chat is deliberately paused. Separate from the automatic
 * breaker in wrebit-bookkeep: that one opens on its own when the provider stops
 * answering and closes itself again, while this is a decision that stays until
 * someone lifts it.
 */
export interface AiChatPauseState {
  paused: boolean;
  /** ISO instant the pause was set. Absent when the agent is running. */
  since?: string;
  /** Internal note. Never reaches the app's users. */
  note?: string;
  /** Absent on a backend that predates the breaker being exposed here. */
  provider?: ProviderState;
}

/**
 * The weekly AI Agent budget every company gets unless one has its own.
 *
 * Null when nobody has set one. That is the shipped state and it means the
 * limit is not enforced at all, so the portal has to be able to say "no budget"
 * rather than render a zero, which would read as "no allowance".
 */
export interface GlobalUsageLimit {
  weeklyCostLimitUsd: number;
  warnAtPercent: number;
  validFrom: string;
  /** Null means "until someone says otherwise". */
  validUntil: string | null;
  reason: string | null;
  changedBy: string | null;
}

/** What one company has actually consumed inside a window. */
export interface CompanyUsage {
  costUsd: number;
  promptTokens: number;
  completionTokens: number;
  cachedPromptTokens: number;
  messageCount: number;
  /**
   * Turns whose model had no price. Real spend the budget cannot see, so a
   * non-zero value means the cost figure beside it is an underestimate.
   */
  unpricedMessageCount: number;
}

export interface UsageWindow {
  start: string;
  end: string;
}

/** The budget in force, and which row decided it. */
export interface EffectiveLimit {
  weeklyCostLimitUsd: number;
  warnAtPercent: number;
  source: 'company' | 'global';
  validUntil?: string | null;
  reason?: string | null;
}

/** One row in the trail of budgets this company has been granted. */
export interface CompanyLimitRecord {
  id: string;
  companyId: string;
  weeklyCostLimitUsd: number;
  validFrom: string;
  validUntil: string | null;
  reason: string;
  changedBy: string | null;
  createdAt: string;
}

export interface CompanyUsageResponse {
  window: UsageWindow;
  usage: CompanyUsage;
  /**
   * The same shape for the week before. A lone figure cannot be judged, and
   * the question in front of a customer is whether this week is unusual for
   * them.
   */
  previousWeek: { window: UsageWindow; usage: CompanyUsage };
  limit: EffectiveLimit | null;
  /**
   * Whether the app would refuse the next turn - the same answer the customer's
   * chat acts on, not a comparison of spend against budget. The two differ by
   * one reservation, which is the end of any week the agent was used.
   */
  blocked: boolean;
  /**
   * Whether the budget itself is gone, as opposed to merely having too little
   * left for one more turn. Absent on a backend that predates the distinction,
   * which is why the note explaining it is written to stay hidden then rather
   * than guess.
   */
  budgetSpent?: boolean;
  /** What one turn must have free to start. Null when no budget is set. */
  minimumTurnCostUsd?: number | null;
  warning: boolean;
  history: CompanyLimitRecord[];
}

/**
 * The message to put next to the field after a failed save.
 *
 * One rule, because this page had three hand-rolled versions of it and the
 * system-prompt save had none:
 *
 * - no connection: say that, since no status means the request never landed;
 * - the server said why: show that, whatever the status. It is refusing this
 *   particular value - below the weekly minimum, too many decimals, no reason
 *   given - and it is always more use than anything this page can guess;
 * - otherwise: the caller's line, which is where "Försök igen" belongs.
 *
 * `err.message` is deliberately not consulted. On an HttpErrorResponse that is
 * Angular's own "Http failure response for <url>: 400 Bad Request", which reads
 * like a server message and tells an admin nothing.
 */
export function saveErrorMessage(err: unknown, fallback: string): string {
  const response = err as HttpErrorResponse | undefined;
  if (response?.status === 0) return 'Kunde inte nå servern.';

  // Two shapes reach here: the routers' own validation sends a plain string on
  // `error`, and throwAichatError sends { code, message }.
  const body = response?.error as
    | { error?: unknown; message?: unknown }
    | undefined;
  const candidates = [
    (body?.error as { message?: unknown } | undefined)?.message,
    body?.message,
    body?.error,
  ];
  const message = candidates.find(
    (value): value is string => typeof value === 'string' && !!value.trim()
  );

  return message ?? fallback;
}

@Injectable({ providedIn: 'root' })
export class AiSettingsService {
  private readonly baseUrl = environment.bookkeepUrl;

  constructor(private http: HttpClient) {}

  getSystemPrompt(): Observable<SystemPromptResponse> {
    return this.http.get<SystemPromptResponse>(
      `${this.baseUrl}/ai-chat-config/system-prompt`
    );
  }

  saveSystemPrompt(prompt: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/ai-chat-config/system-prompt`,
      { prompt }
    );
  }

  getPause(): Observable<AiChatPauseState> {
    return this.http.get<AiChatPauseState>(
      `${this.baseUrl}/ai-chat-config/availability`
    );
  }

  setPause(paused: boolean, note?: string): Observable<AiChatPauseState> {
    return this.http.put<AiChatPauseState>(
      `${this.baseUrl}/ai-chat-config/availability`,
      { paused, ...(note ? { note } : {}) }
    );
  }

  saveModel(model: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/ai-chat-config/model`, {
      model,
    });
  }

  getUsageLimit(): Observable<{ limit: GlobalUsageLimit | null }> {
    return this.http.get<{ limit: GlobalUsageLimit | null }>(
      `${this.baseUrl}/ai-chat-config/usage-limit`
    );
  }

  saveUsageLimit(input: {
    weeklyCostLimitUsd: number;
    warnAtPercent: number;
    reason?: string;
  }): Observable<GlobalUsageLimit> {
    return this.http.put<GlobalUsageLimit>(
      `${this.baseUrl}/ai-chat-config/usage-limit`,
      input
    );
  }

  getCompanyUsage(companyId: string): Observable<CompanyUsageResponse> {
    return this.http.get<CompanyUsageResponse>(
      `${this.baseUrl}/ai-chat-config/usage/${companyId}`
    );
  }

  /**
   * Grants one company its own budget.
   *
   * `validUntil` is omitted rather than sent as null when the caller wants the
   * default. The two are not the same thing to the server: null means "until
   * someone says otherwise", while omitting it lets the raise expire at the end
   * of the current week - which is what a raise for a busy week means.
   */
  saveCompanyLimit(
    companyId: string,
    input: {
      weeklyCostLimitUsd: number;
      reason: string;
      validUntil?: string | null;
    }
  ): Observable<CompanyLimitRecord> {
    return this.http.put<CompanyLimitRecord>(
      `${this.baseUrl}/ai-chat-config/usage/${companyId}/limit`,
      input
    );
  }
}
